import { useEffect, useRef, useState } from "react";
import { Plus, UploadCloud, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

export const FIELD_CLASS =
  "w-full rounded-xl border border-navy-900/15 bg-white px-3.5 py-2.5 text-sm text-navy-900 outline-none transition-colors placeholder:text-slate-400 focus:border-gold-400 focus:ring-2 focus:ring-gold-400/20";

const Label = ({ children }) => <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">{children}</label>;

const RemoveBtn = ({ onClick, label }) => (
  <button type="button" onClick={onClick} aria-label={label} className="shrink-0 rounded-full p-1 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-500">
    <X className="h-3.5 w-3.5" />
  </button>
);

const AddBtn = ({ onClick, children }) => (
  <button type="button" onClick={onClick} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald2-600 hover:underline">
    <Plus className="h-3.5 w-3.5" /> {children}
  </button>
);

// Plain text input bound to a section's heading (or any single string field).
export const TextField = ({ label, value, onChange, placeholder }) => (
  <div>
    <Label>{label}</Label>
    <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={FIELD_CLASS} />
  </div>
);

export const TextAreaField = ({ label, value, onChange, rows = 4 }) => (
  <div>
    <Label>{label}</Label>
    <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows} className={FIELD_CLASS} />
  </div>
);

// Uploads a file to the server and stores the returned URL; a manual URL
// field stays available underneath for pasting an already-hosted image.
export const ImageUploadField = ({ label, value, onChange, token }) => {
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await api.uploadContentImage(token, file);
      onChange(url);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <Label>{label}</Label>
      <div className="flex items-start gap-4">
        {value ? (
          <img src={value} alt="" className="h-20 w-32 shrink-0 rounded-lg border border-slate-200 object-cover" />
        ) : (
          <div className="grid h-20 w-32 shrink-0 place-items-center rounded-lg border border-dashed border-slate-300 text-xs text-slate-400">No image</div>
        )}
        <div className="flex-1 space-y-2">
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-navy-900/15 bg-white px-3.5 py-1.5 text-xs font-semibold text-navy-800 transition-colors hover:border-gold-400 hover:text-gold-600">
            <UploadCloud className="h-3.5 w-3.5" /> {uploading ? "Uploading..." : value ? "Replace Image" : "Upload Image"}
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" disabled={uploading} onChange={(e) => handleFile(e.target.files?.[0])} />
          </label>
          <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Or paste an image URL"
            className={`${FIELD_CLASS} text-xs`}
          />
        </div>
      </div>
    </div>
  );
};

// { heading, items: string[] }
export const HeadingItemsField = ({ label, value, onChange }) => {
  const v = value || { heading: "", items: [] };
  const setItem = (i, text) => onChange({ ...v, items: v.items.map((it, idx) => (idx === i ? text : it)) });
  const removeItem = (i) => onChange({ ...v, items: v.items.filter((_, idx) => idx !== i) });
  const addItem = () => onChange({ ...v, items: [...v.items, ""] });

  return (
    <div className="rounded-xl border border-slate-200 p-4">
      <p className="mb-3 text-sm font-bold text-navy-900">{label}</p>
      <TextField label="Heading" value={v.heading} onChange={(text) => onChange({ ...v, heading: text })} />
      <div className="mt-3 space-y-2">
        {v.items.map((item, i) => (
          <div key={i} className="flex items-center gap-2">
            <input type="text" value={item} onChange={(e) => setItem(i, e.target.value)} className={FIELD_CLASS} />
            <RemoveBtn onClick={() => removeItem(i)} label={`Remove item ${i + 1}`} />
          </div>
        ))}
      </div>
      <AddBtn onClick={addItem}>Add item</AddBtn>
    </div>
  );
};

// { heading, groups: [{ title, items: string[] }] }
export const HeadingGroupsField = ({ label, value, onChange }) => {
  const v = value || { heading: "", groups: [] };
  const setGroup = (i, group) => onChange({ ...v, groups: v.groups.map((g, idx) => (idx === i ? group : g)) });
  const removeGroup = (i) => onChange({ ...v, groups: v.groups.filter((_, idx) => idx !== i) });
  const addGroup = () => onChange({ ...v, groups: [...v.groups, { title: "", items: [] }] });

  return (
    <div className="rounded-xl border border-slate-200 p-4">
      <p className="mb-3 text-sm font-bold text-navy-900">{label}</p>
      <TextField label="Heading" value={v.heading} onChange={(text) => onChange({ ...v, heading: text })} />
      <div className="mt-3 space-y-4">
        {v.groups.map((g, i) => (
          <div key={i} className="rounded-lg bg-[rgb(238,242,250)]/60 p-3">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={g.title}
                onChange={(e) => setGroup(i, { ...g, title: e.target.value })}
                placeholder="Group title (e.g. Proprietorship)"
                className={FIELD_CLASS}
              />
              <RemoveBtn onClick={() => removeGroup(i)} label={`Remove group ${i + 1}`} />
            </div>
            <div className="mt-2 space-y-1.5 pl-1">
              {g.items.map((item, j) => (
                <div key={j} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => setGroup(i, { ...g, items: g.items.map((it, idx) => (idx === j ? e.target.value : it)) })}
                    className={FIELD_CLASS}
                  />
                  <RemoveBtn onClick={() => setGroup(i, { ...g, items: g.items.filter((_, idx) => idx !== j) })} label={`Remove document ${j + 1}`} />
                </div>
              ))}
              <AddBtn onClick={() => setGroup(i, { ...g, items: [...g.items, ""] })}>Add document</AddBtn>
            </div>
          </div>
        ))}
      </div>
      <AddBtn onClick={addGroup}>Add group</AddBtn>
    </div>
  );
};

// { heading, steps: [{ title, desc }] }
export const HeadingStepsField = ({ label, value, onChange }) => {
  const v = value || { heading: "", steps: [] };
  const setStep = (i, step) => onChange({ ...v, steps: v.steps.map((s, idx) => (idx === i ? step : s)) });
  const removeStep = (i) => onChange({ ...v, steps: v.steps.filter((_, idx) => idx !== i) });
  const addStep = () => onChange({ ...v, steps: [...v.steps, { title: "", desc: "" }] });

  return (
    <div className="rounded-xl border border-slate-200 p-4">
      <p className="mb-3 text-sm font-bold text-navy-900">{label}</p>
      <TextField label="Heading" value={v.heading} onChange={(text) => onChange({ ...v, heading: text })} />
      <div className="mt-3 space-y-3">
        {v.steps.map((s, i) => (
          <div key={i} className="rounded-lg bg-[rgb(238,242,250)]/60 p-3">
            <div className="flex items-center gap-2">
              <input type="text" value={s.title} onChange={(e) => setStep(i, { ...s, title: e.target.value })} placeholder="Step title" className={FIELD_CLASS} />
              <RemoveBtn onClick={() => removeStep(i)} label={`Remove step ${i + 1}`} />
            </div>
            <textarea
              value={s.desc}
              onChange={(e) => setStep(i, { ...s, desc: e.target.value })}
              placeholder="Step description"
              rows={2}
              className={`${FIELD_CLASS} mt-2`}
            />
          </div>
        ))}
      </div>
      <AddBtn onClick={addStep}>Add step</AddBtn>
    </div>
  );
};

// { heading, body } or { heading, note } depending on textKey
export const HeadingTextField = ({ label, value, onChange, textKey = "body" }) => {
  const v = value || { heading: "", [textKey]: "" };
  return (
    <div className="rounded-xl border border-slate-200 p-4">
      <p className="mb-3 text-sm font-bold text-navy-900">{label}</p>
      <TextField label="Heading" value={v.heading} onChange={(text) => onChange({ ...v, heading: text })} />
      <div className="mt-3">
        <TextAreaField label="Text" value={v[textKey]} onChange={(text) => onChange({ ...v, [textKey]: text })} rows={3} />
      </div>
    </div>
  );
};

// [{ q, a }]
export const FaqsField = ({ label, value, onChange }) => {
  const v = value || [];
  const setFaq = (i, faq) => onChange(v.map((f, idx) => (idx === i ? faq : f)));
  const removeFaq = (i) => onChange(v.filter((_, idx) => idx !== i));
  const addFaq = () => onChange([...v, { q: "", a: "" }]);

  return (
    <div className="rounded-xl border border-slate-200 p-4">
      <p className="mb-3 text-sm font-bold text-navy-900">{label}</p>
      <div className="space-y-3">
        {v.map((f, i) => (
          <div key={i} className="rounded-lg bg-[rgb(238,242,250)]/60 p-3">
            <div className="flex items-center gap-2">
              <input type="text" value={f.q} onChange={(e) => setFaq(i, { ...f, q: e.target.value })} placeholder="Question" className={FIELD_CLASS} />
              <RemoveBtn onClick={() => removeFaq(i)} label={`Remove FAQ ${i + 1}`} />
            </div>
            <textarea
              value={f.a}
              onChange={(e) => setFaq(i, { ...f, a: e.target.value })}
              placeholder="Answer"
              rows={2}
              className={`${FIELD_CLASS} mt-2`}
            />
          </div>
        ))}
      </div>
      <AddBtn onClick={addFaq}>Add FAQ</AddBtn>
    </div>
  );
};

// string[] edited as comma-separated slugs
export const SlugsField = ({ label, value, onChange }) => (
  <div>
    <Label>{label}</Label>
    <input
      type="text"
      value={(value || []).join(", ")}
      onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
      placeholder="e.g. llp, private-limited-company"
      className={FIELD_CLASS}
    />
  </div>
);

// WYSIWYG body editor for rich-text blog posts. Uses contentEditable plus
// document.execCommand rather than an editor dependency — the toolbar below is
// the whole feature set, and the server sanitises the HTML on save.
// ponytail: execCommand is deprecated (works in every current browser). Swap in
// TipTap if the toolbar ever needs tables, images or undo history.
const RICH_TOOLS = [
  { cmd: "formatBlock", arg: "<h2>", label: "H2" },
  { cmd: "formatBlock", arg: "<h3>", label: "H3" },
  { cmd: "formatBlock", arg: "<p>", label: "P" },
  { cmd: "bold", label: "B", className: "font-bold" },
  { cmd: "italic", label: "I", className: "italic" },
  { cmd: "underline", label: "U", className: "underline" },
  { cmd: "insertUnorderedList", label: "• List" },
  { cmd: "insertOrderedList", label: "1. List" },
  { cmd: "formatBlock", arg: "<blockquote>", label: "Quote" },
  { cmd: "removeFormat", label: "Clear" },
];

export const RichTextField = ({ label, value, onChange }) => {
  const ref = useRef(null);

  // Only seed the DOM when the incoming value differs, otherwise React would
  // reset the caret to the start on every keystroke.
  useEffect(() => {
    if (ref.current && value !== ref.current.innerHTML) ref.current.innerHTML = value || "";
  }, [value]);

  const exec = (cmd, arg) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    onChange(ref.current?.innerHTML || "");
  };

  const addLink = () => {
    const url = window.prompt("Link URL (https://...)");
    if (url) exec("createLink", url);
  };

  return (
    <div>
      <Label>{label}</Label>
      <div className="overflow-hidden rounded-xl border border-navy-900/15 bg-white">
        <div className="flex flex-wrap gap-1 border-b border-navy-900/10 bg-[rgb(238,242,250)]/70 p-2">
          {RICH_TOOLS.map((t) => (
            <button
              key={t.label}
              type="button"
              onClick={() => exec(t.cmd, t.arg)}
              className={`rounded-md px-2.5 py-1 text-xs font-semibold text-navy-800 transition-colors hover:bg-white ${t.className || ""}`}
            >
              {t.label}
            </button>
          ))}
          <button type="button" onClick={addLink} className="rounded-md px-2.5 py-1 text-xs font-semibold text-navy-800 transition-colors hover:bg-white">
            Link
          </button>
        </div>
        <div
          ref={ref}
          contentEditable
          suppressContentEditableWarning
          onInput={(e) => onChange(e.currentTarget.innerHTML)}
          onBlur={(e) => onChange(e.currentTarget.innerHTML)}
          data-testid="rich-text-editor"
          className="prose-blog min-h-[24rem] max-h-[36rem] overflow-y-auto px-4 py-3 text-sm outline-none"
        />
      </div>
      <p className="mt-1.5 text-xs text-slate-400">Headings, bold, lists and links are kept. Anything else is stripped on save.</p>
    </div>
  );
};
